import React, { useState, useRef } from 'react';
import { Form, Formik } from 'formik';
import { useParams } from 'react-router-dom';

import { TextField, TextArea } from 'components/Form/Fields';
import oWnApi from 'utils/WnApi';
import { SubmitButton } from 'components/Form/Buttons';
import usePageTitle from 'utils/hooks/usePageTitle';
import useWindowSize from 'utils/hooks/useWindowSize';
import ContinueButton from 'components/Authentication/AuthLayout/ContinueButton';
import helpRequestSchema from './schemas';
import AuthLayout from '../../AuthLayout';
import feliciaHelpLogo from '../../images/felicia-help.svg';
import HelpPageTitle from './HelpTitle';

const oInitialValues = {
  firstname: '',
  lastname: '',
  email: '',
  comments: '',
};

/**
 * Help form for unauthenticated users.
 */
const SignUpHelp = () => {
  const oWindowSize = useWindowSize();
  const bIsMobile = oWindowSize.width < 640;
  const [bIsSuccessState, fnSetIsSuccessState] = useState(false);
  const [bIsContinueDisabled, fnSetIsContinueDisabled] = useState(true);
  const oParams = useParams();
  const oFormRef = useRef();
  const sMatchType = oParams.type;
  const bIsParent = sMatchType === 'parent';
  usePageTitle('Help');

  const fnHandleSubmit = async () => {
    if (oFormRef.current) {
      const oFormData = oFormRef.current.values;
      const oRequestPayload = { ...oFormData, contactmethod: 'email' };
      const oResult = await oWnApi.post('contact_us', oRequestPayload, false);
      if (oResult.status === 200) {
        fnSetIsSuccessState(true);
      }
    }
  };

  // fnHandleSubmitDisable handles disabling the continue button when the form is invalid or dirty,
  // only when changed from the initial values.
  const fnHandleSubmitDisable = (formikProps) => {
    fnSetIsContinueDisabled(!(formikProps?.isValid && formikProps?.dirty));
  };

  const HelpForm = bIsSuccessState ? (
    <>
      <h1 className='authHelpForm__successHeading'>
        Request Successfully Sent
      </h1>
      <p className='authHelpForm__successText'>
        Someone from Wake Forest will be in contact with you soon!
      </p>
    </>
  ) : (
    <Formik
      innerRef={oFormRef}
      onSubmit={fnHandleSubmit}
      initialValues={oInitialValues}
      validateOnBlur
      validationSchema={helpRequestSchema}
    >
      {(formikProps) => (
        <Form
          className='help__form'
          onChange={fnHandleSubmitDisable(formikProps)}
        >
          <TextField sLabel='First Name' sName='firstname' sId='first-name' />
          <TextField sLabel='Last Name' sName='lastname' sId='last-name' />
          <TextField
            sLabel='Email'
            sName='email'
            sPlaceholder='you@example.com'
            sId='email'
          />
          <TextArea sId='comments' sName='comments' sLabel='Comments' />
          {bIsParent && !bIsMobile && (
            <SubmitButton className='help__parentSubmit'>
              Send Request
            </SubmitButton>
          )}
        </Form>
      )}
    </Formik>
  );

  return (
    <AuthLayout
      bContinueButtonDisabled={bIsContinueDisabled}
      continueButton={
        bIsParent && !bIsMobile ? null : (
          <ContinueButton
            fnHandleContinue={fnHandleSubmit}
            bContinueButtonDisabled={bIsContinueDisabled}
            sContinueButtonText='Send Request'
          />
        )
      }
      left={
        <div className='help__formContainer'>
          <HelpPageTitle sMatchType={sMatchType} />
          {!bIsParent && HelpForm}
        </div>
      }
      right={
        bIsParent ? (
          <div className='help__formContainer'>
            <p className='help__instructions'>
              Tell us more about the problem. We&apos;ll look into it and reach
              out via email soon.
            </p>
            {HelpForm}
          </div>
        ) : (
          <img
            src={feliciaHelpLogo}
            className='help__feliciaHelpLogo'
            alt='Wake Network help logo'
          />
        )
      }
    />
  );
};

export default SignUpHelp;
